@if (loading()) {
  <div class="flex justify-content-center align-items-center p-6">
    <i class="pi pi-spin pi-spinner text-4xl"></i>
  </div>
} @else if (system()) {
  <div class="approved-system-view-container p-4" style="position: relative;">
    <!-- Loading Overlay for Certificate Generation -->
    @if (generatingCertificate()) {
      <div class="certificate-loading-overlay">
        <div class="flex flex-column align-items-center">
          <p-progressspinner></p-progressspinner>
          <p class="mt-3 text-600">Generating certificate...</p>
        </div>
      </div>
    }
    <!-- Header with Actions -->
    <div class="flex justify-content-between align-items-center pb-1 mb-3 mt-1 border-bottom-1 border-300">
      <h2 class="m-0 text-lg font-semibold">System Information</h2>
      <div class="flex gap-2">
        @if (canRenew()) {
          <button
            pButton
            type="button"
            class="p-button-text p-button-sm"
            (click)="onRenewCertificate()"
            pTooltip="Renew certificate expiry date"
            tooltipPosition="top"><i class="pi pi-calendar-plus" pButtonIcon></i><span pButtonLabel>Renew</span>
          </button>
        }
        @if (hasVisibleActions()) {
          <app-actions-menu
            [items]="actionMenuItems()"
            tooltip="Actions"
            testId="system-actions-menu">
          </app-actions-menu>
        }
      </div>
    </div>

    <!-- Basic Information Section -->
    <div class="mb-4">
      <div class="grid">
        <div class="col-12 md:col-6">
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Approval Number</label>
            <div class="text-900 font-medium">{{ system()!.completeApprovalNumber }}</div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">System Name</label>
            @if (canManageSystem()) {
              <app-inline-text-input
                [content]="system()!.system || ''"
                testId="system-name-input"
                (valueChanged)="onSystemNameChanged($event)">
              </app-inline-text-input>
            } @else {
              <div class="text-900">{{ system()!.system || '-' }}</div>
            }
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">System Type</label>
            <div class="text-900">{{ system()!.systemType || '-' }}</div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Date of Granting</label>
            <div class="text-900">{{ formatDate(getEffectiveApprovedSince(system())) }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Manufacturer / Supplier</label>
            <div class="text-900">{{ system()!.supplierOrManufacturerOrganisation?.name || '-' }}</div>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Status</label>
            <p-tag
              [value]="system()!.approvalState || 'Granting'"
              [severity]="getStatusSeverity()"
              [rounded]="true">
            </p-tag>
          </div>
          <div class="field mb-3">
            <label class="block text-sm font-medium text-600 mb-1">Certificate Expiry Date</label>
            <div class="text-900">{{ formatDate(system()!.certificateExpiryDate) }}</div>
          </div>
          @if (system()!.cancellationReason) {
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Cancellation Reason</label>
              <div class="text-900">{{ system()!.cancellationReason }}</div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Pre-Treatment Attributes Section (only for A-, AP-, AN- systems) -->
    @if (!isCoatingSystem()) {
      <div class="mb-4">
        <div class="flex justify-content-between align-items-center mb-3">
          <h3 class="text-lg font-semibold m-0">
            <i class="pi pi-cog mr-2"></i>
            Pre-Treatment Attributes
          </h3>
          @if (canManageSystem()) {
            <button
              pButton
              type="button"
              class="p-button-text p-button-sm"
              (click)="onEditAttributes()"><i class="pi pi-pencil" pButtonIcon></i><span pButtonLabel>Edit Attributes</span>
            </button>
          }
        </div>
        <p-divider></p-divider>
        <div class="grid">
          <div class="col-12 md:col-6">
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Rinse Process</label>
              <div class="text-900">{{ getPretreatmentSystem()?.rinseProcess || '-' }}</div>
            </div>
          </div>
          <div class="col-12 md:col-6">
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Outdoor Exposure</label>
              <div class="text-900">
                @if (getPretreatmentSystem()?.outdoorExposureTestStartDate) {
                  {{ calculateOutdoorExposureYears(getPretreatmentSystem()!.outdoorExposureTestStartDate!) }} years completed
                } @else {
                  -
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Powder Coating Attributes Section (for CoatingSystem / P- and DP- systems) -->
    @if (isCoatingSystem()) {
      <div class="mb-4">
        <div class="flex justify-content-between align-items-center mb-3">
          <h3 class="text-lg font-semibold m-0">
            <i class="pi pi-cog mr-2"></i>
            Powder Coating Attributes
          </h3>
          @if (canManageSystem()) {
            <button
              pButton
              type="button"
              class="p-button-text p-button-sm"
              (click)="onEditPowderAttributes()"><i class="pi pi-pencil" pButtonIcon></i><span pButtonLabel>Edit Attributes</span>
            </button>
          }
        </div>
        <p-divider></p-divider>
        <app-powder-attributes-section
          [system]="getCoatingSystemData()"
          [showHeader]="false"
          [editable]="canManageSystem()"
          (addColor)="onAddColor()"
          (editColor)="onEditColorRestriction($event)">
        </app-powder-attributes-section>
      </div>
    }

    <!-- Production Site Section -->
    @if (system()!.productionSite) {
      <div class="mb-4">
        <div class="flex justify-content-between align-items-center mb-3">
          <h3 class="text-lg font-semibold m-0">
            <i class="pi pi-building mr-2"></i>
            Production Site
          </h3>
          @if (canManageSystem()) {
            <button
              pButton
              type="button"
              class="p-button-text p-button-sm"
              (click)="onEditProductionSite()"><i class="pi pi-pencil" pButtonIcon></i><span pButtonLabel>Edit Production Site</span>
            </button>
          }
        </div>
        <p-divider></p-divider>
        <div class="grid">
          <div class="col-12 md:col-6">
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Site Name</label>
              <div class="text-900">{{ system()!.productionSite!.name || '-' }}</div>
            </div>
            @if (system()!.productionSite!.customer) {
              <div class="field mb-3">
                <label class="block text-sm font-medium text-600 mb-1">Customer</label>
                <div class="text-900">{{ system()!.productionSite!.customer!.name || '-' }}</div>
              </div>
            }
          </div>
          <div class="col-12 md:col-6">
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Address</label>
              <div class="surface-card p-3 border-round shadow-2">
                <div class="flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    @if (system()!.productionSite!.addressLine1) {
                      <div class="text-900">{{ system()!.productionSite!.addressLine1 }}</div>
                    }
                    @if (system()!.productionSite!.addressLine2) {
                      <div class="text-900">{{ system()!.productionSite!.addressLine2 }}</div>
                    }
                    @if (system()!.productionSite!.postalCode || system()!.productionSite!.city) {
                      <div class="text-900 mt-2">
                        {{ system()!.productionSite!.postalCode }} {{ system()!.productionSite!.city }}
                      </div>
                    }
                    @if (system()!.productionSite!.countryName) {
                      <div class="text-900">{{ system()!.productionSite!.countryName }}</div>
                    }
                  </div>
                  <button
                    pButton
                    type="button"
                    class="p-button-text p-button-sm p-button-rounded ml-2"
                    style="width: 1.75rem; height: 1.75rem; min-width: 1.75rem; margin-top: -5px;"
                    pTooltip="Show on map"
                    tooltipPosition="top"
                    (click)="openMap()"><i class="pi pi-map-marker" pButtonIcon></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Certificates Section -->
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-3">
        <i class="pi pi-file-pdf mr-2"></i>
        Certificates
      </h3>
      <p-divider></p-divider>

      <div class="certificates-list">
        @if (latestCertificate(); as certificate) {
          <div class="surface-100 border-round overflow-hidden mb-2">
            <div class="p-3 flex align-items-center gap-3">
              <div class="flex align-items-center justify-content-center border-round bg-blue-100"
                   style="width: 3rem; height: 3rem;">
                <i class="pi pi-file-pdf text-xl text-blue-600"></i>
              </div>
              <div class="flex-1">
                <div class="font-medium mb-1">
                  {{ certificate.fileName }}
                </div>
                <div class="text-400 text-sm">
                  <span class="mr-3">{{ certificate.documentType }}</span>
                  <span class="mr-3">{{ formatFileSize(certificate.fileSize) }}</span>
                  <span>{{ formatDate(certificate.createdAt) }}</span>
                </div>
              </div>
              <div class="flex gap-2">
                @if (certificate.objectStoreRef) {
                  <p-button
                    icon="pi pi-eye"
                    severity="secondary"
                    [text]="true"
                    [rounded]="true"
                    pTooltip="View certificate"
                    tooltipPosition="top"
                    (onClick)="viewCertificate(certificate)">
                  </p-button>
                  <p-button
                    icon="pi pi-download"
                    severity="secondary"
                    [text]="true"
                    [rounded]="true"
                    pTooltip="Download certificate"
                    tooltipPosition="top"
                    (onClick)="downloadCertificate(certificate)">
                  </p-button>
                  <app-language-dropdown
                    (languageSelected)="downloadCertificateInLanguage($event)">
                  </app-language-dropdown>
                }
              </div>
            </div>
          </div>

          @if (olderCertificates().length > 0) {
            <p-accordion>
              <p-accordion-panel value="older">
                <p-accordion-header>
                  Previous versions ({{ olderCertificates().length }})
                </p-accordion-header>
                <p-accordion-content>
                  @for (olderCert of olderCertificates(); track olderCert.id) {
                    <div class="surface-100 border-round overflow-hidden mb-2">
                      <div class="p-3 flex align-items-center gap-3">
                        <div class="flex align-items-center justify-content-center border-round bg-blue-100"
                             style="width: 3rem; height: 3rem;">
                          <i class="pi pi-file-pdf text-xl text-blue-600"></i>
                        </div>
                        <div class="flex-1">
                          <div class="font-medium mb-1">
                            {{ olderCert.fileName }}
                          </div>
                          <div class="text-400 text-sm">
                            <span class="mr-3">{{ olderCert.documentType }}</span>
                            <span class="mr-3">{{ formatFileSize(olderCert.fileSize) }}</span>
                            <span>{{ formatDate(olderCert.createdAt) }}</span>
                          </div>
                        </div>
                        <div class="flex gap-2">
                          @if (olderCert.objectStoreRef) {
                            <p-button
                              icon="pi pi-eye"
                              severity="secondary"
                              [text]="true"
                              [rounded]="true"
                              pTooltip="View certificate"
                              tooltipPosition="top"
                              (onClick)="viewCertificate(olderCert)">
                            </p-button>
                            <p-button
                              icon="pi pi-download"
                              severity="secondary"
                              [text]="true"
                              [rounded]="true"
                              pTooltip="Download certificate"
                              tooltipPosition="top"
                              (onClick)="downloadCertificate(olderCert)">
                            </p-button>
                            <app-language-dropdown
                              (languageSelected)="downloadCertificateInLanguage($event)">
                            </app-language-dropdown>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </p-accordion-content>
              </p-accordion-panel>
            </p-accordion>
          }
        } @else {
          <div class="text-center text-500 p-4">
            <i class="pi pi-inbox text-4xl mb-3 block"></i>
            <div>No certificates available</div>
            @if (canManageSystem() && system()!.approvalState && ['Provisional', 'Interim', 'Final'].includes(system()!.approvalState!)) {
              <div class="mt-2">
                <button
                  pButton
                  type="button"
                  class="p-button-text p-button-sm"
                  (click)="onGenerateCertificate()"><i class="pi pi-file-pdf" pButtonIcon></i><span pButtonLabel>Generate Certificate</span>
                </button>
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Running Florida Reports Section (QCT or responsible-lab tag) -->
    @if (canSeeFlorida()) {
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-3">
          <i class="pi pi-sun mr-2"></i>
          Outdoor Exposure
        </h3>
        <p-divider></p-divider>

        <p-accordion (valueChange)="onFloridaAccordion($event)">
          <p-accordion-panel value="florida">
            <p-accordion-header>
              Running Florida Reports ({{ runningFloridaCount() ?? 0 }})
            </p-accordion-header>
            <p-accordion-content>
              @if ((runningFloridaCount() ?? 0) === 0) {
                <div class="text-center text-500 p-3">
                  <i class="pi pi-inbox text-2xl mb-2 block"></i>
                  <div>No running Florida Reports</div>
                </div>
              } @else {
                @for (report of runningFloridaReports(); track report.id) {
                  <div class="surface-100 border-round overflow-hidden mb-2">
                    <div class="p-3 flex align-items-center gap-3">
                      <div class="flex-1">
                        <div class="font-medium mb-1">
                          Cert. year {{ report.certificationYear }} · Class {{ report.class }}
                        </div>
                        <div class="text-400 text-sm flex align-items-center gap-2">
                          <p-tag [value]="report.state" severity="info"></p-tag>
                          <span>Current cycle: {{ floridaCycleLabel(report) }}</span>
                        </div>
                      </div>
                      <p-button
                        icon="pi pi-external-link"
                        severity="secondary"
                        [text]="true"
                        [rounded]="true"
                        pTooltip="Open Florida Report"
                        tooltipPosition="top"
                        (onClick)="viewFloridaReport(report.id)">
                      </p-button>
                    </div>
                  </div>
                }
              }
            </p-accordion-content>
          </p-accordion-panel>
        </p-accordion>
      </div>
    }

    <!-- Bills Section -->
    <div class="mb-4">
      <app-bills-table
        [systemId]="system()!.id"
        [systemType]="isCoatingSystem() ? 'CoatingSystem' : 'PretreatmentSystem'">
      </app-bills-table>
    </div>

    <!-- Documents Section -->
    <div class="mb-4">
      <h3 class="text-lg font-semibold mb-3">
        <i class="pi pi-folder-open mr-2"></i>
        Documents
      </h3>
      <p-divider></p-divider>

      <!-- Documents List -->
      @if (documents().length > 0) {
        <div class="documents-list mb-3">
          @for (document of documents(); track document.id) {
            <div class="surface-100 border-round overflow-hidden mb-2">
              <div class="p-3 flex align-items-center gap-3">
                <!-- File Type Icon -->
                <div class="flex align-items-center justify-content-center border-round"
                     [ngClass]="'bg-' + getFileTypeColor(document) + '-100'"
                     style="width: 3rem; height: 3rem;">
                  <fa-icon [icon]="getFileTypeIcon(document)"
                           [ngClass]="'text-' + getFileTypeColor(document) + '-600'"
                           class="text-xl"></fa-icon>
                </div>

                <!-- Document Info -->
                <div class="flex-1">
                  <div class="font-medium mb-1">{{ document.fileName }}</div>
                  <div class="text-400 text-sm">
                    <span class="mr-3">{{ getDocumentTypeLabel(document.documentType) }}</span>
                    <span class="mr-3">{{ formatFileSize(document.fileSize) }}</span>
                    <span>{{ formatDate(document.createdAt) }}</span>
                  </div>
                </div>

                <!-- Document Actions -->
                <div class="flex gap-2">
                  @if (document.objectStoreRef) {
                    @if (isViewableDocument(document)) {
                      <p-button
                        icon="pi pi-eye"
                        severity="secondary"
                        [text]="true"
                        [rounded]="true"
                        pTooltip="View document"
                        tooltipPosition="top"
                        (onClick)="viewDoc(document)">
                      </p-button>
                    }
                    <p-button
                      icon="pi pi-download"
                      severity="secondary"
                      [text]="true"
                      [rounded]="true"
                      pTooltip="Download document"
                      tooltipPosition="top"
                      (onClick)="downloadDoc(document)">
                    </p-button>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      }

      <!-- Upload Drop Zone -->
      @if (canManageSystem()) {
        <div class="surface-100 border-round p-3">
          <app-document-uploader
            [(files)]="selectedFiles"
            [multiple]="true">
          </app-document-uploader>

          @if (selectedFiles.length > 0) {
            <div class="mt-3">
              <p-button
                label="Upload {{ selectedFiles.length }} document(s)"
                icon="pi pi-upload"
                [loading]="uploadingDocuments()"
                [disabled]="uploadingDocuments()"
                (onClick)="onUploadDocuments()"
                severity="primary">
              </p-button>
            </div>
          }
        </div>
      }
    </div>

    <!-- Archive Information -->
    @if (system()!.isArchived) {
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-3">
          <i class="pi pi-inbox mr-2"></i>
          Archive Information
        </h3>
        <p-divider></p-divider>
        <div class="grid">
          <div class="col-12 md:col-6">
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Archived Date</label>
              <div class="text-900">{{ formatDate(system()!.archivedAt) }}</div>
            </div>
          </div>
          <div class="col-12 md:col-6">
            <div class="field mb-3">
              <label class="block text-sm font-medium text-600 mb-1">Archived By</label>
              <div class="text-900">{{ system()!.archivedBy || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
    }
  </div>
} @else {
  <div class="flex flex-column align-items-center justify-content-center p-6">
    <i class="pi pi-exclamation-triangle text-4xl text-warning mb-3"></i>
    <div class="text-lg">System not found</div>
  </div>
}
